import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "@/lib/data";

const navLinks = [
  { label: "Services", href: "/#services" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Service Area", href: "/#service-area" },
  { label: "Careers", href: "/careers" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", href: "/#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.04)]" : "bg-white/80 backdrop-blur"
      }`}
    >
      <div className="container-pad mx-auto max-w-7xl flex items-center justify-between h-20">
        <Link to="/" onClick={handleLogoClick} data-testid="header-logo" className="flex items-center gap-3 group">
          <div className="w-12 h-12 rounded-full bg-navy flex items-center justify-center overflow-hidden ring-1 ring-navy/10 group-hover:ring-teal-500 transition">
            <img src={COMPANY.logoUrl} alt="Comfort Care Transportation" className="w-full h-full object-cover" />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-heading font-semibold text-navy text-lg">Comfort Care</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-teal-600">NEMT · VA · DC · MD</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-navy/80 hover:text-teal-600 transition relative after:absolute after:bottom-[-6px] after:left-0 after:h-0.5 after:w-0 after:bg-teal-500 hover:after:w-full after:transition-all"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="header-phone"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-teal-600 transition"
          >
            <Phone className="w-4 h-4 text-teal-500" />
            {COMPANY.phone}
          </a>
          <a href="/#booking" data-testid="header-cta-book" className="hidden md:inline-flex btn-primary !py-2.5 !px-5 text-sm">
            Request a Ride
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-full bg-sand-100"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5 text-navy" /> : <Menu className="w-5 h-5 text-navy" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-gray-100">
          <div className="container-pad py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-navy font-medium py-2 border-b border-gray-100"
              >
                {l.label}
              </a>
            ))}
            <a href={`tel:${COMPANY.phoneRaw}`} className="btn-outline mt-2">
              <Phone className="w-4 h-4" /> Call {COMPANY.phone}
            </a>
            <a href="/#booking" onClick={() => setOpen(false)} className="btn-primary">
              Request a Ride
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
